import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatCard } from "@/components/ui/stat-card";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Calendar,
  Banknote,
  XCircle,
  TrendingUp,
  Users,
  Loader2,
  PieChart,
  Download,
  Trophy,
  CheckCircle,
  Clock,
  Receipt,
  LayoutList,
} from "lucide-react";
import { useBusiness } from "@/hooks/use-business";
import { useAnalytics } from "@/hooks/use-analytics";
import { useMembershipAnalytics } from "@/hooks/use-membership-analytics";
import { useCateringAnalytics } from "@/hooks/use-catering-analytics";
import { useLogBookExpenses } from "@/hooks/use-log-book-expenses";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

type ReportRange = "1m" | "3m" | "6m" | "12m";

const REPORT_RANGE_OPTIONS: Array<{ value: ReportRange; label: string; months: number }> = [
  { value: "1m", label: "1 month", months: 1 },
  { value: "3m", label: "3 months", months: 3 },
  { value: "6m", label: "6 months", months: 6 },
  { value: "12m", label: "1 year", months: 12 },
];

// Currency helper
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    GHS: "₵",
    NGN: "₦",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

const formatCurrency = (amount: number, currency: string = "GHS"): string => {
  return `${getCurrencySymbol(currency)}${amount.toFixed(2)}`;
};

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--secondary))",
  "hsl(var(--accent))",
  "hsl(var(--muted-foreground))",
  "hsl(var(--destructive))",
  "#8b5cf6", // purple
  "#06b6d4", // cyan
  "#f59e0b", // amber
];

const statusColors: Record<string, string> = {
  completed: "hsl(var(--primary))",
  confirmed: "hsl(var(--primary))",
  pending: "#f59e0b", // amber
  cancelled: "hsl(var(--destructive))",
  no_show: "hsl(var(--muted-foreground))",
  in_progress: "#06b6d4", // cyan
  rescheduled: "#8b5cf6", // purple
};

export default function Analytics() {
  const { business, loading: businessLoading } = useBusiness();
  const primaryModule = ((business as any)?.primary_module || "appointments") as string;
  const isMemberships = primaryModule === "memberships";
  const isCateringServices = primaryModule === "catering_services" || primaryModule === "food_vendors";
  const appointmentsInvoicingEnabled = (business as any)?.appointments_invoicing_enabled === true;
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [reportRange, setReportRange] = useState<ReportRange>("1m");
  const reportDateRange = useMemo(() => {
    const option = REPORT_RANGE_OPTIONS.find((item) => item.value === reportRange) || REPORT_RANGE_OPTIONS[0];
    const to = new Date();
    const from = new Date(to);
    from.setMonth(from.getMonth() - option.months);
    return { from, to };
  }, [reportRange]);
  const { data: aptData, loading: analyticsLoading, error, categories } = useAnalytics({
    categoryId: categoryFilter,
    from: reportDateRange.from,
    to: reportDateRange.to,
  });
  const { data: memData, loading: memAnalyticsLoading, error: memError } = useMembershipAnalytics();
  const { data: catData, loading: catLoading, error: catError } = useCateringAnalytics();
  const isMobile = useIsMobile();
  const [trendView, setTrendView] = useState<"weekly" | "monthly">("weekly");
  const logBookEnabled = (business as any)?.log_book_enabled === true;
  const { totalExpensesThisMonth, loading: expensesLoading } = useLogBookExpenses();

  const isLoading =
    businessLoading || (isMemberships ? memAnalyticsLoading : isCateringServices ? catLoading : analyticsLoading);
  const data = isMemberships ? (memData as any) : isCateringServices ? (catData as any) : (aptData as any);
  const effectiveError = isMemberships ? memError : isCateringServices ? catError : error;

  const formatCurrencyValue = (amount: number) => {
    return formatCurrency(amount, business?.currency || "GHS");
  };

  const trendRows =
    trendView === "weekly"
      ? (data.weekdayTrendsWeek || []).map((d: any) => ({ label: d.day, count: d.count }))
      : (data.monthlyTrends6Months || []).map((m: any) => ({ label: m.month, count: m.count }));
  const maxWeekdayCount = useMemo(() => {
    const max = Math.max(1, ...trendRows.map((d) => d.count || 0));
    return max;
  }, [trendRows]);

  const maxServicePopularityCount = useMemo(() => {
    const rows = ((data as any)?.servicePopularity || []) as Array<{ count?: number }>;
    return Math.max(1, ...rows.map((s) => Number(s?.count || 0)));
  }, [data]);

  const downloadCSV = (filename: string, rows: Record<string, any>[]) => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const esc = (v: any) => {
      const s = v == null ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <PageHeader title="Analytics" description="Track your business performance" />
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  if (effectiveError) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <PageHeader
          title="Reports"
          description={
            isMemberships
              ? "Membership & access insights"
              : isCateringServices
                ? "Orders, meals & feedback insights"
                : "Appointments & revenue insights"
          }
        />
        <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6 text-center">
          <p className="text-destructive">{effectiveError}</p>
        </div>
      </AppShell>
    );
  }

  if (isCateringServices) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <PageHeader
          title="Reports"
          description="Orders, items & delivery insights"
        />

        <div className="space-y-6 sm:space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard title="Orders (7d)" value={String(data.orders7d || 0)} icon={Calendar} />
            <StatCard title="Orders (30d)" value={String(data.orders30d || 0)} icon={TrendingUp} />
            <StatCard title="Revenue (30d)" value={formatCurrencyValue(Number(data.revenue30d || 0))} icon={Banknote} />
            <StatCard title="Active customers" value={String(data.activeCustomers30d || 0)} icon={Users} />
            {logBookEnabled && (
              <StatCard
                title="Total Expenses (Month)"
                description="From log book"
                value={expensesLoading ? "—" : formatCurrencyValue(totalExpensesThisMonth)}
                icon={Receipt}
              />
            )}
          </div>

          <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
            <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
              <CardTitle className="text-base sm:text-lg">Orders trend</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Last 14 days</CardDescription>
            </CardHeader>
            <CardContent className="h-[260px] sm:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.ordersOverTime14d || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} interval={isMobile ? 2 : 0} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
                <CardTitle className="text-base sm:text-lg">Top items</CardTitle>
                <CardDescription className="text-xs sm:text-sm">By quantity (last 30 days)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 p-4 sm:p-6">
                {(data.topItems || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">No data yet.</div>
                ) : (
                  (data.topItems || []).map((m: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between gap-3 rounded-xl border border-border/80 bg-muted/20 p-3 sm:p-4">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{m.item}</div>
                        <div className="text-xs text-muted-foreground">Revenue: {formatCurrencyValue(Number(m.revenue || 0))}</div>
                      </div>
                      <div className="text-sm font-semibold">{Number(m.qty || 0)}</div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
                <CardTitle className="text-base sm:text-lg">Top customers</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Most orders (last 30 days)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 p-4 sm:p-6">
                {(data.topCustomers || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">No data yet.</div>
                ) : (
                  (data.topCustomers || []).map((c: any, idx: number) => (
                    <div key={idx} className="flex items-start justify-between gap-3 rounded-xl border border-border/80 bg-muted/20 p-3 sm:p-4">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.customer || "—"}</div>
                        <div className="text-xs text-muted-foreground truncate">{c.contact || "—"}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">{Number(c.orders || 0)} orders</div>
                        <div className="text-xs text-muted-foreground">{formatCurrencyValue(Number(c.revenue || 0))}</div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
                <CardTitle className="text-base sm:text-lg">Fulfillment split</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Delivery vs pickup (last 30 days)</CardDescription>
              </CardHeader>
              <CardContent className="h-[300px] sm:h-[320px] p-4 sm:p-6">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie 
                      data={data.fulfillmentSplit30d || []} 
                      dataKey="count" 
                      nameKey="label" 
                      cx="50%"
                      cy="40%"
                      labelLine={{
                        stroke: "hsl(var(--muted-foreground))",
                        strokeWidth: 1,
                      }}
                      label={({ label, count }) => `${label}: ${count}`}
                      innerRadius={isMobile ? 35 : 45}
                      outerRadius={isMobile ? 70 : 85}
                      stroke="hsl(var(--card))"
                      strokeWidth={2}
                    >
                      {(data.fulfillmentSplit30d || []).map((d: any, index: number) => {
                        const label = String(d?.label || "").toLowerCase();
                        const fill =
                          label === "delivery"
                            ? "hsl(var(--primary))"
                            : label === "pickup"
                              ? "#f59e0b" // amber (more visible than secondary on light backgrounds)
                              : COLORS[index % COLORS.length];
                        return <Cell key={index} fill={fill} />;
                      })}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload[0]) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-card border border-border rounded-lg p-2 shadow-lg">
                              <p className="font-medium text-sm">{data.label}</p>
                              <p className="text-xs text-muted-foreground">{data.count} orders</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
                <CardTitle className="text-base sm:text-lg">Order status distribution</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Last 30 days</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 p-4 sm:p-6">
                {(data.statusDistribution30d || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">No orders yet.</div>
                ) : (
                  (data.statusDistribution30d || []).slice(0, 10).map((t: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between gap-3 rounded-xl border border-border/80 bg-muted/20 p-3 sm:p-4">
                      <div className="font-medium capitalize">{String(t.status || "new").replace(/_/g, " ")}</div>
                      <div className="text-sm font-semibold">{Number(t.count || 0)}</div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Reports"
        description={isMemberships ? "Membership & access insights" : "Appointments & revenue insights"}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {!isMemberships && (
              <Select
                value={categoryFilter ?? "all"}
                onValueChange={(v) => setCategoryFilter(v === "all" ? null : v)}
              >
                <SelectTrigger className="w-[180px] sm:w-[200px]">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {(categories || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="uncategorized">Uncategorized</SelectItem>
                </SelectContent>
              </Select>
            )}
            {!isMemberships && (
              <Select value={reportRange} onValueChange={(value) => setReportRange(value as ReportRange)}>
                <SelectTrigger className="w-[150px] sm:w-[170px]">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_RANGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />

      <div className="space-y-6 sm:space-y-8">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          title={isMemberships ? "Visits This Week" : "Bookings This Week"}
          value={String(isMemberships ? data.visitsThisWeek : data.bookingsThisWeek)}
          icon={Calendar}
        />
        <StatCard
          title={isMemberships ? "Visits This Month" : "Bookings in Period"}
          value={String(isMemberships ? data.visitsThisMonth : data.bookingsThisMonth)}
          icon={Calendar}
        />
        <StatCard
          title={isMemberships ? "Total Revenue (Month)" : "Total Revenue (Period)"}
          value={formatCurrencyValue(isMemberships ? (data.totalRevenueThisMonth ?? 0) : (data.totalRevenueThisMonth ?? 0))}
          icon={Banknote}
        />
        <StatCard
          title={isMemberships ? "Expiring Soon" : "No-Show Rate"}
          value={isMemberships ? String(data.expiringSoon ?? 0) : `${data.noShowRate}%`}
          icon={XCircle}
        />
        {logBookEnabled && (
          <StatCard
            title="Total Expenses (Month)"
            description="From log book"
            value={expensesLoading ? "—" : formatCurrencyValue(totalExpensesThisMonth)}
            icon={Receipt}
          />
        )}
      </div>

      {/* Revenue Breakdown */}
      <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
        <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
          <CardTitle className="text-base sm:text-lg tracking-tight">Revenue Breakdown</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Revenue by source {isMemberships ? "(this month)" : "(selected period)"}
          </CardDescription>
        </CardHeader>
        <CardContent className={isMobile ? "p-4" : "p-6"}>
          <div className="space-y-3 sm:space-y-4">
            {!isMemberships && (
              <div className="flex flex-col gap-2 rounded-xl border border-border/80 bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-medium text-foreground">Appointments</div>
                  <div className="text-xs text-muted-foreground">Completed & confirmed bookings in period</div>
                </div>
                <div className="text-lg font-semibold text-foreground tabular-nums">
                  {formatCurrencyValue((data as any).revenueThisMonth ?? 0)}
                </div>
              </div>
            )}
            {isMemberships && (
              <div className="flex flex-col gap-2 rounded-xl border border-border/80 bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-medium text-foreground">Walk-ins</div>
                  <div className="text-xs text-muted-foreground">Day pass payments</div>
                </div>
                <div className="text-lg font-semibold text-foreground tabular-nums">
                  {formatCurrencyValue((data as any).walkInRevenueThisMonth ?? 0)}
                </div>
              </div>
            )}
            {(data as any).membershipRevenueThisMonth > 0 && (
              <div className="flex flex-col gap-2 rounded-xl border border-border/80 bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-medium text-foreground">Memberships</div>
                  <div className="text-xs text-muted-foreground">Renewal payments</div>
                </div>
                <div className="text-lg font-semibold text-foreground tabular-nums">
                  {formatCurrencyValue((data as any).membershipRevenueThisMonth ?? 0)}
                </div>
              </div>
            )}
            {appointmentsInvoicingEnabled && (
              <div className="flex flex-col gap-2 rounded-xl border border-border/80 bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-medium text-foreground">Invoices</div>
                  <div className="text-xs text-muted-foreground">Paid invoices</div>
                </div>
                <div className="text-lg font-semibold text-foreground tabular-nums">
                  {formatCurrencyValue((data as any).invoiceRevenueThisMonth ?? 0)}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-2 rounded-xl border-2 border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-semibold text-foreground">Total Revenue</div>
                <div className="text-xs text-muted-foreground">
                  {isMemberships ? "All sources combined" : "All sources in selected period"}
                </div>
              </div>
              <div className="text-xl font-bold text-foreground tabular-nums">
                {formatCurrencyValue((data as any).totalRevenueThisMonth ?? 0)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isMemberships ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-6">
            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
                <CardTitle className="text-base sm:text-lg">Members</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Active + churn + expiring</CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-border bg-card p-3">
                    <div className="text-xs text-muted-foreground">Active</div>
                    <div className="mt-2 text-2xl font-semibold text-foreground">{String(data.activeMembers ?? 0)}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-3">
                    <div className="text-xs text-muted-foreground">Expiring</div>
                    <div className="mt-2 text-2xl font-semibold text-foreground">{String(data.expiringSoon ?? 0)}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">Next 7 days</div>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-3">
                    <div className="text-xs text-muted-foreground">Churned</div>
                    <div className="mt-2 text-2xl font-semibold text-foreground">{String(data.churned30d ?? 0)}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">Last 30 days</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
                <CardTitle className="text-base sm:text-lg">Visits split</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Members vs walk-ins (this month)</CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {(() => {
                  const members = Number(data.memberVisitsThisMonth ?? 0);
                  const walkins = Number(data.walkInVisitsThisMonth ?? 0);
                  const total = Math.max(1, members + walkins);
                  const memberPct = Math.round((members / total) * 100);
                  const walkPct = 100 - memberPct;
                  return (
                    <div className="space-y-3">
                      <div className="h-3 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${memberPct}%` }} />
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <div className="text-muted-foreground">Members</div>
                        <div className="font-medium text-foreground">
                          {members} ({memberPct}%)
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <div className="text-muted-foreground">Walk-ins</div>
                        <div className="font-medium text-foreground">
                          {walkins} ({walkPct}%)
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
                <CardTitle className="text-base sm:text-lg">Top plans</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Active members by plan</CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {(data.topPlans || []).length ? (
                  <div className="space-y-2">
                    {(data.topPlans || []).map((p: any, idx: number) => (
                      <div key={idx} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/10 px-3 py-2">
                        <div className="text-sm text-foreground truncate">{p.plan_name}</div>
                        <div className="text-sm font-medium text-foreground">{p.active_members}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">No plan data yet.</div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
            <CardHeader className="pb-3 sm:pb-4 border-b border-border/60 bg-muted/20">
              <CardTitle className="text-base sm:text-lg">Walk-in payments</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Revenue by payment method (this month)</CardDescription>
            </CardHeader>
            <CardContent className={isMobile ? "p-4" : "p-0"}>
              {isMobile ? (
                <div className="space-y-3">
                  {(data.walkInByPaymentMethod || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">No walk-in payments yet.</div>
                  ) : (
                    (data.walkInByPaymentMethod || []).map((m: any, idx: number) => (
                      <div key={idx} className="rounded-xl border border-border p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="font-semibold text-foreground capitalize">{String(m.method || "").replace(/_/g, " ")}</div>
                          <div className="text-sm font-medium text-foreground">{formatCurrencyValue(Number(m.revenue || 0))}</div>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">{m.count} payments</div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="w-full overflow-x-auto">
                  <table className="min-w-[520px] w-full text-sm">
                    <thead className="border-b border-border">
                      <tr className="text-left">
                        <th className="p-4 font-semibold">Method</th>
                        <th className="p-4 font-semibold">Payments</th>
                        <th className="p-4 font-semibold">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.walkInByPaymentMethod || []).map((m: any, idx: number) => (
                        <tr key={idx} className="border-b border-border last:border-b-0">
                          <td className="p-4 font-medium capitalize">{String(m.method || "").replace(/_/g, " ")}</td>
                          <td className="p-4">{m.count}</td>
                          <td className="p-4">{formatCurrencyValue(Number(m.revenue || 0))}</td>
                        </tr>
                      ))}
                      {(data.walkInByPaymentMethod || []).length === 0 ? (
                        <tr>
                          <td className="p-4 text-muted-foreground" colSpan={3}>
                            No walk-in payments yet.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Revenue over time */}
      {!isMemberships ? (
        <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
          <CardHeader className="border-b border-border/60 bg-muted/20 pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
              </span>
              Revenue over time
            </CardTitle>
            <CardDescription className="pl-11 text-xs sm:pl-12 sm:text-sm">
              Appointment revenue by week (last 12 weeks, Mon–Sun buckets)
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            {data.revenueOverTime.length > 0 ? (
              <div className="w-full overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
                <ResponsiveContainer width="100%" minWidth={300} height={isMobile ? 240 : 280}>
                  <LineChart data={data.revenueOverTime} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "currentColor" }}
                      angle={isMobile ? -45 : -35}
                      textAnchor="end"
                      height={isMobile ? 56 : 52}
                      interval={isMobile ? 1 : 0}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "currentColor" }}
                      width={52}
                      tickFormatter={(value) => {
                        const symbol = getCurrencySymbol(business?.currency || "GHS");
                        return value >= 1000 ? `${symbol}${(value / 1000).toFixed(1)}k` : `${symbol}${value}`;
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        fontSize: "12px",
                        padding: "8px",
                      }}
                      labelFormatter={(label) => `Week of ${label}`}
                      formatter={(value: number) => [formatCurrencyValue(value), "Revenue"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--primary))", r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex min-h-[160px] items-center justify-center text-sm text-muted-foreground">
                No weekly revenue data yet.
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Popular services & appointment status */}
      {!isMemberships ? (
        <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
          <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
            <CardHeader className="border-b border-border/60 bg-muted/20 pb-3 sm:pb-4">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <LayoutList className="h-4 w-4 sm:h-5 sm:w-5" />
                </span>
                Popular services
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm pl-11 sm:pl-12">Ranked by booking count</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-4 sm:p-6">
              {data.servicePopularity.length > 0 ? (
                data.servicePopularity.map((svc: { name: string; count: number }, idx: number) => {
                  const pct = Math.round((Number(svc.count || 0) / maxServicePopularityCount) * 100);
                  return (
                    <div key={`${svc.name}-${idx}`} className="rounded-xl border border-border/80 bg-muted/20 p-3 sm:p-4">
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span className="min-w-0 truncate font-medium text-foreground">{svc.name}</span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">{svc.count}</span>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex min-h-[160px] items-center justify-center text-sm text-muted-foreground">
                  No service data yet.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
            <CardHeader className="border-b border-border/60 bg-muted/20 pb-3 sm:pb-4">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <PieChart className="h-4 w-4 sm:h-5 sm:w-5" />
                </span>
                Appointment status
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm pl-11 sm:pl-12">Share of appointments by status</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              {data.statusDistribution.length > 0 ? (
              <div className="w-full">
                <ResponsiveContainer width="100%" height={isMobile ? 260 : 300}>
                  <RechartsPieChart>
                    <Pie
                      data={data.statusDistribution}
                      cx="50%"
                      cy="40%"
                      labelLine={{
                        stroke: "hsl(var(--muted-foreground))",
                        strokeWidth: 1,
                      }}
                      label={({ status, count, percent }) => {
                        const statusLabel = status
                          .split('_')
                          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ');
                        return `${statusLabel}: ${count}`;
                      }}
                      outerRadius={isMobile ? 70 : 85}
                      innerRadius={isMobile ? 35 : 45}
                      fill="#8884d8"
                      dataKey="count"
                      stroke="hsl(var(--card))"
                      strokeWidth={2}
                    >
                      {data.statusDistribution.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={statusColors[entry.status] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload[0]) {
                          const data = payload[0].payload;
                          const statusLabel = data.status
                            .split('_')
                            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');
                          return (
                            <div className="bg-card border border-border rounded-lg p-2 shadow-lg">
                              <p className="font-medium text-sm">{statusLabel}</p>
                              <p className="text-xs text-muted-foreground">{data.count} appointments</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground sm:h-[250px]">
                <p>No status data available</p>
              </div>
            )}
            </CardContent>
        </Card>
      </div>
      ) : null}

      {/* Weekly/Monthly Trends (weekday distribution) */}
      <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
        <CardHeader className="border-b border-border/60 bg-muted/20 pb-3 sm:pb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
                </span>
                Trends
              </CardTitle>
              <CardDescription className="pl-11 text-xs sm:pl-12 sm:text-sm">
                {trendView === "weekly"
                  ? isMemberships
                    ? "Visits by weekday (last 8 weeks)"
                    : "Bookings by weekday (last 8 weeks)"
                  : isMemberships
                    ? "Visits by month (last 6 months)"
                    : "Bookings by month (last 6 months)"}
              </CardDescription>
            </div>
            <div className="inline-flex w-full overflow-hidden rounded-lg border border-border sm:w-auto">
              <button
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap ${trendView === "weekly" ? "bg-primary text-primary-foreground" : "bg-background"}`}
                onClick={() => setTrendView("weekly")}
                type="button"
              >
                Weekly
              </button>
              <button
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap ${trendView === "monthly" ? "bg-primary text-primary-foreground" : "bg-background"}`}
                onClick={() => setTrendView("monthly")}
                type="button"
              >
                Monthly
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 p-4 sm:p-6">
          {trendRows.length > 0 ? (
            trendRows.map((d) => {
              const pct = Math.round((d.count / maxWeekdayCount) * 100);
              return (
                <div key={d.label} className="rounded-xl border border-border/80 bg-muted/20 p-3 sm:p-4">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 font-medium text-foreground">{d.label}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">{d.count}</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex min-h-[120px] items-center justify-center text-sm text-muted-foreground">
              No trend data yet.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service Performance */}
      {!isMemberships ? (
      <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
        <CardHeader className="flex flex-col gap-4 border-b border-border/60 bg-muted/20 pb-4 sm:flex-row sm:items-start sm:justify-between sm:pb-4">
          <div className="min-w-0 space-y-1">
            <CardTitle className="text-base sm:text-lg">Service type performance</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Revenue and booking metrics by service type
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full shrink-0 sm:w-auto"
            onClick={() => downloadCSV("service-performance.csv", data.servicePerformance as any)}
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </CardHeader>
          <CardContent className={isMobile ? "p-4" : "p-0"}>
            {isMobile ? (
              <div className="space-y-3">
                {data.servicePerformance.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No service data available</div>
                ) : (
                  data.servicePerformance.map((s) => (
                    <div key={s.serviceId} className="rounded-xl border border-border/80 bg-muted/20 p-4">
                      <div className="font-semibold text-foreground">{s.serviceName}</div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-muted-foreground">Bookings</div>
                          <div className="font-medium">{s.bookings}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Revenue</div>
                          <div className="font-medium">{formatCurrencyValue(s.revenue)}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Avg Price</div>
                          <div className="font-medium">{formatCurrencyValue(s.avgPrice)}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Completion</div>
                          <div className="font-medium">{s.completionRate.toFixed(1)}%</div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="min-w-[760px] w-full text-sm">
                  <thead className="border-b border-border">
                    <tr className="text-left">
                      <th className="p-4 font-semibold">Service Type</th>
                      <th className="p-4 font-semibold">Bookings</th>
                      <th className="p-4 font-semibold">Revenue</th>
                      <th className="p-4 font-semibold">Avg Price</th>
                      <th className="p-4 font-semibold">Completion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.servicePerformance.map((s) => (
                      <tr key={s.serviceId} className="border-b border-border last:border-b-0">
                        <td className="p-4 font-medium">{s.serviceName}</td>
                        <td className="p-4">{s.bookings}</td>
                        <td className="p-4">{formatCurrencyValue(s.revenue)}</td>
                        <td className="p-4">{formatCurrencyValue(s.avgPrice)}</td>
                        <td className="p-4">{s.completionRate.toFixed(1)}%</td>
                      </tr>
                    ))}
                    {data.servicePerformance.length === 0 && (
                      <tr>
                        <td className="p-4 text-muted-foreground" colSpan={5}>
                          No service data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Staff Performance */}
      {!isMemberships ? (
      <div className="space-y-4 sm:space-y-6">
        <div className="rounded-2xl border border-border/80 bg-muted/15 px-4 py-5 sm:px-6">
          <h2 className="text-lg font-semibold tracking-tight text-foreground">Service provider performance</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Team summary and per-provider metrics for the current reporting period.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          <StatCard
            title="Active Providers"
            description="Providers on your team"
            value={String(data.staffSummary.activeProviders)}
            icon={Users}
          />
          <StatCard
            title="Top Performer"
            description={formatCurrencyValue(data.staffSummary.topPerformerRevenue) + " revenue"}
            value={data.staffSummary.topPerformerName || "—"}
            icon={Trophy}
          />
          <StatCard
            title="Avg Completion Rate"
            description="Across all providers"
            value={`${data.staffSummary.avgCompletionRate.toFixed(1)}%`}
            icon={CheckCircle}
          />
          <StatCard
            title="Hours Worked (Month)"
            description="From clock in/out"
            value={`${(
              data.staffPerformance.reduce((sum, s) => sum + (s.hoursWorkedFromClock ?? 0), 0) || 0
            ).toFixed(1)}h`}
            icon={Clock}
          />
        </div>

        <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
          <CardHeader className="border-b border-border/60 bg-muted/20 pb-3 sm:pb-4">
            <CardTitle className="text-base sm:text-lg">Provider breakdown</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Bookings, revenue, and hours by provider</CardDescription>
          </CardHeader>
          <CardContent className={isMobile ? "p-4" : "p-0"}>
            {isMobile ? (
              <div className="space-y-3">
                {data.staffPerformance.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No staff performance data available</div>
                ) : (
                  data.staffPerformance.map((s) => (
                    <div key={s.staffId} className="rounded-xl border border-border/80 bg-muted/20 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-semibold text-foreground">{s.staffName}</div>
                        <div className="text-sm font-medium text-foreground">{formatCurrencyValue(s.revenue)}</div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-muted-foreground">Bookings</div>
                          <div className="font-medium">{s.bookings}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Completion</div>
                          <div className="font-medium">{s.completionRate.toFixed(1)}%</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Hours (booked)</div>
                          <div className="font-medium">{s.hours.toFixed(1)}h</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Hours worked</div>
                          <div className="font-medium">{(s.hoursWorkedFromClock ?? 0).toFixed(1)}h</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Clock-ins</div>
                          <div className="font-medium">{s.clockInsCount ?? 0}</div>
                          {s.lastClockAt && (
                            <div className="text-xs text-muted-foreground">
                              Last: {format(new Date(s.lastClockAt), "MMM d, h:mm a")}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">Specialties:</span>{" "}
                        {s.specialties.length ? s.specialties.join(", ") : "—"}
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="min-w-[980px] w-full text-sm">
                  <thead className="border-b border-border">
                    <tr className="text-left">
                      <th className="p-4 font-semibold">Provider</th>
                      <th className="p-4 font-semibold">Bookings</th>
                      <th className="p-4 font-semibold">Revenue</th>
                      <th className="p-4 font-semibold">Completion</th>
                      <th className="p-4 font-semibold">Hours (booked)</th>
                      <th className="p-4 font-semibold">Hours worked</th>
                      <th className="p-4 font-semibold">Clock-ins</th>
                      <th className="p-4 font-semibold">Specialties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.staffPerformance.map((s) => (
                      <tr key={s.staffId} className="border-b border-border last:border-b-0">
                        <td className="p-4 font-medium">{s.staffName}</td>
                        <td className="p-4">{s.bookings}</td>
                        <td className="p-4">{formatCurrencyValue(s.revenue)}</td>
                        <td className="p-4">{s.completionRate.toFixed(1)}%</td>
                        <td className="p-4">{s.hours.toFixed(1)}h</td>
                        <td className="p-4">{(s.hoursWorkedFromClock ?? 0).toFixed(1)}h</td>
                        <td className="p-4">
                          {s.clockInsCount ?? 0}
                          {s.lastClockAt && (
                            <span className="block text-xs text-muted-foreground">
                              Last: {format(new Date(s.lastClockAt), "MMM d, h:mm a")}
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-muted-foreground">
                          {s.specialties.length ? s.specialties.join(", ") : "—"}
                        </td>
                      </tr>
                    ))}
                    {data.staffPerformance.length === 0 && (
                      <tr>
                        <td className="p-4 text-muted-foreground" colSpan={8}>
                          No staff performance data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      ) : null}
      </div>
    </AppShell>
  );
}
